// What should printing do for this outlet — the single answer every POS print site
// asks for, so the sale receipt, the sale return and the direct return cannot drift
// apart the way the four inlined `receiptType === 2 || === 3` forks did.
//
// Shops that run a thermal roll and an A4 printer side by side decide at the counter
// which one a customer gets, so Outlet > Settings can turn the silent print into a
// prompt (AskReceiptFormatOnSale) and say which printers actually exist
// (OfferThermalReceipt / OfferA4Receipt / OfferA5Receipt).

// Same values as Outlet.ReceiptType — 1=Thermal, 2=A4, 3=A5.
export const RECEIPT_FORMAT = { thermal: 1, a4: 2, a5: 3 };

// Offer order is fixed (thermal, A4, A5) so the buttons never reshuffle under a
// cashier's finger between sales.
const OFFERS = [
  { format: RECEIPT_FORMAT.thermal, flag: "offerThermalReceipt" },
  { format: RECEIPT_FORMAT.a4, flag: "offerA4Receipt" },
  { format: RECEIPT_FORMAT.a5, flag: "offerA5Receipt" },
];

/**
 * @param {object} outlet the hydrated outlet off the sale / activeReg blob
 * @returns {{ask: boolean, formats: number[], preferred: number}}
 *   ask       – show the format prompt instead of printing silently
 *   formats   – the formats to offer, in display order
 *   preferred – the format to pre-select, and the one printed when ask is false
 */
export const resolveReceiptFormats = (outlet) => {
  // The outlet's configured format stays the default in both modes. Anything
  // unrecognised (or a tenant whose DB predates these columns) falls back to
  // thermal, which is the entity default.
  const configured = [RECEIPT_FORMAT.thermal, RECEIPT_FORMAT.a4, RECEIPT_FORMAT.a5]
    .includes(outlet?.receiptType)
    ? outlet.receiptType
    : RECEIPT_FORMAT.thermal;

  const formats = OFFERS.filter((o) => outlet?.[o.flag]).map((o) => o.format);

  // A prompt with one button is not a choice, and one with none is a dead end.
  // Both fall back to a silent print — as does an outlet on a tenant DB that has
  // not been synced yet, where the toggle reads undefined. That is the whole
  // backward-compatibility guarantee: absent columns == today's behaviour.
  const ask = outlet?.askReceiptFormatOnSale === true && formats.length > 1;

  return {
    ask,
    formats: ask ? formats : [configured],
    preferred: ask && !formats.includes(configured) ? formats[0] : configured,
  };
};

/** DOM id of the hidden mount holding one format's rendered receipt. */
export const receiptDivId = (baseId, format) => `${baseId}_${format}`;

// ── Designed templates ───────────────────────────────────────────────────────────
//
// A document type can have a user-designed template assigned to the outlet printing it.
// Resolution lives here, beside resolveReceiptFormats, because this file is already the
// single answer to "what should printing do for this outlet" — the four inlined
// `receiptType === 2 || === 3` forks that preceded it are why.

/** Document types a designed template can be written for. */
export const DOC_TYPE = { salesInvoice: "SALES_INVOICE" };

/**
 * The template this outlet should use for a document type, or null for the built-in
 * layout.
 *
 * NULL IS THE IMPORTANT RETURN. An outlet with no assignment — which is every outlet
 * until someone opts in, and every outlet on a tenant database that has not been synced
 * yet — renders the existing hand-written template untouched. That is what makes the
 * whole feature additive: no live client's paper can move because of a template they
 * never created.
 *
 * @param {object} outlet the hydrated outlet off the sale payload
 * @param {string} docType one of DOC_TYPE
 */
/**
 * The template body shape this build can render — EXACTLY this, in both directions.
 *
 * Declared here rather than imported from the invoice renderer on purpose: this file is a
 * dependency-free leaf that 17 receipt templates import, and pulling the renderer in would
 * drag it into every one of those bundles. A test asserts this equals
 * INVOICE_SCHEMA_VERSION, so the two cannot drift.
 */
export const SUPPORTED_SCHEMA_VERSION = 3;

export const resolveDocTemplate = (outlet, docType) => {
  if (!docType) return null;
  const assigned = outlet?.docTemplates;
  if (!assigned || typeof assigned !== "object") return null;

  const template = assigned[docType];
  // isActive false is a template the org has retired without deleting; it must fall back
  // to the built-in rather than printing nothing.
  if (!template || template.isActive === false) return null;
  if (!template.bodyJson && !template.body) return null;

  // A body this build cannot render — NEWER OR OLDER — falls back to the built-in layout.
  //
  // Newer: a browser on a stale bundle would render a shape it does not understand, find
  // nothing it recognises, and send a BLANK invoice to the printer with no error.
  //
  // Older: v1 stored a flat list of bands and v2 stored pieces at absolute coordinates.
  // Neither is migrated to v3, deliberately. There is no client-authored data in either
  // shape — one dev-made row exists across every tenant database — and converting a
  // coordinate model into a layout model means guessing which of seven layouts the shop
  // meant. A known-good built-in invoice beats a guess, and beats a blank sheet.
  //
  // The check lives here rather than in the renderer because this is the one place that
  // decides "designed template or built-in", and returning null is already the documented
  // way to say "use the built-in".
  if (Number(template.schemaVersion) !== SUPPORTED_SCHEMA_VERSION) return null;

  return template;
};
